import { Injectable, NgZone } from '@angular/core';
import * as moment from 'moment';
import { Subscription, timer } from 'rxjs';
import { LoggerService } from '../logger/logger.service';
import { LocalStorageService } from '../storage/local-storage/local-storage.service';
import { SyncService } from './sync.service';

const NEXT_SYNC_WAIT_TIME_MS = 60 * 10 * 1000; // 10 minutes
const SYNC_WAIT_TIME_MS = 30 * 1000; // 30 seconds

@Injectable({
	providedIn: 'root'
})
export class SyncTimerService {
	lastSyncTime: string = null;
	timerSubscription: Subscription = null;

	constructor(
		private storageService: LocalStorageService,
		private logger: LoggerService,
		private syncService: SyncService,
		private ngZone: NgZone
	) { }

	async periodicSync() {
		// Check again after timer if currently syncing
		if (this.syncService.isSyncing$.value) {
			this.startTimerForPeriodicSync(SYNC_WAIT_TIME_MS);
			return;
		}

		// Don't allow this method to be called if we are waiting on a timer
		if (!this.timerSubscription) {
			this.lastSyncTime = await this.storageService.sync.getItem('lastSyncTime');
			if (!this.lastSyncTime) {
				this.startTimerForPeriodicSync(NEXT_SYNC_WAIT_TIME_MS);
			} else {
				const elapsedTimeMs = moment.duration(moment().diff(this.lastSyncTime)).asMilliseconds();
				if (elapsedTimeMs >= NEXT_SYNC_WAIT_TIME_MS) {
					const minutesSinceLastSync = elapsedTimeMs / 1000 / 60;
					this.logger.logTrace(`${minutesSinceLastSync} minutes since last sync.  Auto-syncing`, { minutesSinceLastSync });
					this.ngZone.run(async () => {
						await this.syncService.sync();
						this.startTimerForPeriodicSync(NEXT_SYNC_WAIT_TIME_MS);
					});
				} else {
					const timeUntilNextSync = NEXT_SYNC_WAIT_TIME_MS - elapsedTimeMs;
					this.startTimerForPeriodicSync(timeUntilNextSync);
				}
			}
		}
	}

	private startTimerForPeriodicSync(time: number) {
		const syncTimerPeriod = time / 1000 / 60;
		this.logger.logTrace(`Starting periodic sync timer for ${syncTimerPeriod} minutes`, { syncTimerPeriod });
		this.ngZone.runOutsideAngular(() => {
			this.timerSubscription = timer(time).subscribe(() => {
				this.timerSubscription = null;
				this.periodicSync();
			});
		});
	}
}
